import { Input, InputProps } from 'antd';
import * as React from 'react';
import { useCallback } from 'react';
import { useInputController } from '../hooks/useInputController';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility';
import { useFormItemEnabled } from '../item/useFormItemEnabled';

export interface ITextInputProps extends Omit<InputProps, 'name' | 'onChange'> {
  name: string;
  onChange?: (value: string | null | undefined) => void;
}

export function TextInput(props: ITextInputProps) {
  const { name, onChange: onAfterChange, onBlur: onAfterBlur, ...inputProps } = props;

  const { enabled, layoutEnabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e);
      if (onAfterChange) {
        onAfterChange(e.target.value);
      }
    },
    [onChange, onAfterChange],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur();
      onAfterBlur?.(e);
    },
    [onBlur, onAfterBlur],
  );

  const accessibility = useFormItemAccessibility();

  return (
    <Input
      suffix={<span aria-hidden="true" />} // See https://ant.design/components/input#why-input-lose-focus-when-change-prefixsuffixshowcount
      size="large"
      disabled={enabled === false || layoutEnabled === false}
      {...accessibility.attributes}
      {...inputProps}
      value={inputProps.value ?? value}
      ref={ref}
      onChange={handleChange}
      onBlur={handleBlur}
      name={name}
    />
  );
}
